import type { LanguageCode } from "@/config/i18n";
import { defaultLanguage, isValidLanguage } from "@/config/i18n";

export const LANGUAGE_COOKIE_NAME = "preferred_language";

/**
 * Parse an Accept-Language header into language subtags ordered by preference.
 *
 * @example parseAcceptLanguage("ko-KR,ko;q=0.9,en-US;q=0.8") // ["ko", "ko", "en"]
 */
export function parseAcceptLanguage(
  header: string | null | undefined,
): string[] {
  if (!header) return [];

  return header
    .split(",")
    .map((part, index) => {
      const [tag, ...params] = part.trim().split(";");
      const qParam = params.find((p) => p.trim().startsWith("q="));
      const q = qParam ? Number(qParam.trim().slice(2)) : 1;

      return {
        lang: tag.trim().toLowerCase().split("-")[0],
        q: Number.isFinite(q) ? q : 0,
        index,
      };
    })
    .filter((entry) => entry.lang && entry.lang !== "*" && entry.q > 0)
    .sort((a, b) => b.q - a.q || a.index - b.index)
    .map((entry) => entry.lang);
}

/**
 * Pick the UI language for a request without a language prefix:
 * explicit user choice (cookie) first, then the browser's preferences.
 */
export function negotiateLanguage({
  cookieValue,
  acceptLanguage,
}: {
  cookieValue?: string | null;
  acceptLanguage?: string | null;
}): LanguageCode {
  if (cookieValue && isValidLanguage(cookieValue)) return cookieValue;

  for (const lang of parseAcceptLanguage(acceptLanguage)) {
    if (isValidLanguage(lang)) return lang;
  }

  return defaultLanguage;
}
